import React from "react";
import { scaleLinear } from "d3-scale";
import { line, curveCardinal } from "d3-shape";
import { extent, group } from "d3-array";
import { wordwrap } from "d3-jetpack";

const RemittanceFlowChart = ({ props }) => {
  // const { width, height } = props

  const width = props.width;
  const height = props.height;
  const margin = {
    top: 50,
    right: 20,
    bottom: 50,
    left: 50,
  };

  const w = width - margin.right - margin.left;
  const h = height - margin.top - margin.bottom;

  const categories = [
    "Foreign direct investment",
    "Remittances",
    "Portfolio debt and equity flows",
    "Official development assistance",
  ];
  const categoryTranslations = [
    props.fdi,
    props.remittances,
    props.portfolio,
    props.oda,
  ];

  const colors = ["#4eaea4", "#ed6663", "#43658b", "#ffa372"];

  const data = props.data["remittance-flows"].reduce((acc, cur) => {
    categories.forEach((d) => {
      if (cur[d]) {
        acc.push({
          year: cur.year,
          type: d,
          value: cur[d],
        });
      }
    });
    return acc;
  }, []);

  const x = scaleLinear()
    .domain(extent(data, (d) => d.year))
    .range([0, w]);

  const y = scaleLinear()
    .domain(extent(data, (d) => d.value))
    .range([h, 0]);

  const l = line()
    .x((d) => x(d.year))
    .y((d) => y(d.value))
    .curve(curveCardinal);

  return (
    <div>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <g transform={`translate(${margin.left}, ${margin.top})`}>
          <g className="y-axis">
            {y
              .nice()
              .ticks(5)
              .map((d) => (
                <g key={`y-tick-${d}`} transform={`translate(0, ${y(d)})`}>
                  <line
                    x1={x.range()[0]}
                    x2={x.range()[1]}
                    stroke="#ccc"
                    style={{ strokeWidth: 0.5 }}
                  />
                  <text
                    dx={-5}
                    style={{
                      fontSize: "12px",
                      textAnchor: "end",
                      dominantBaseline: "central",
                    }}
                  >
                    {d}
                  </text>
                </g>
              ))}
            <text
              transform={`translate(${x.range()[0]}, ${
                (y.range()[0] + y.range()[1]) / 2
              }) rotate(-90)`}
              style={{
                textAnchor: "middle",
                fontSize: 12,
              }}
              dy={-30}
            >
              {props.usdBillions}
            </text>
          </g>
          <g className="lexft-axis" transform={`translate(0, ${y.range()[0]})`}>
            {[1990, 1995, 2000, 2005, 2010, 2015, 2019, 2021].map((d) => (
              <g key={`x-tick-${d}`} transform={`translate(${x(d)}, 0)`}>
                <line stroke="#ccc" y1={3} y2={-3} />
                <text
                  dy={18}
                  style={{
                    fontSize: "12px",
                    textAnchor: d === 2019 ? "end" : "middle",
                  }}
                >
                  {d}
                </text>
              </g>
            ))}
          </g>
          <rect
            fill="black"
            opacity={0.1}
            x={x(2019)}
            width={x(2021) - x(2019)}
            height={h}
          />
          <text
            transform={`translate(${x(2020)}, 0)`}
            dx={0}
            dy={-10}
            style={{
              textAnchor: "middle",
              fontSize: 12,
              dominantBaseline: "central",
            }}
            fill="#777"
          >
            {props.expected}
          </text>
          <clipPath id="historical">
            <rect x={x(1990)} width={x(2019) - x(1990)} height={h} />
          </clipPath>
          <clipPath id="projected">
            <rect x={x(2019)} width={x(2021) - x(2019)} height={h} />
          </clipPath>
          {Array.from(group(data, (d) => d.type)).map((t, i) => (
            <g key={`type-${i}`}>
              <path
                d={l(t[1])}
                fill="none"
                stroke={colors[categories.indexOf(t[0])]}
                style={{ strokeWidth: i === 1 ? 3 : 1.5 }}
                clipPath="url(#historical)"
              />
              <path
                d={l(t[1])}
                fill="none"
                stroke={colors[categories.indexOf(t[0])]}
                style={{
                  strokeWidth: i === 1 ? 3 : 1.5,
                  strokeDasharray: "2 2",
                }}
                clipPath="url(#projected)"
              />
            </g>
          ))}
          <g>
            {Array.from(group(data, (d) => d.type)).map((t, i) => (
              <text
                key={`label=${i}`}
                style={{
                  dominantBaseline: "central",
                  fontSize: 12,
                  textAnchor: "end",
                  fontWeight: "bold",
                }}
                dy={i === 1 ? 8 : 0}
                fill={colors[categories.indexOf(t[0])]}
              >
                {wordwrap(categoryTranslations[i], 30).map((token, k) => (
                  <tspan
                    key={`token-${k}`}
                    x={x(2005)}
                    y={10 + i * 32 + k * 16}
                  >
                    {token}
                  </tspan>
                ))}
              </text>
            ))}
          </g>
        </g>
      </svg>
    </div>
  );
};

export default RemittanceFlowChart;
